import { WorkspaceProfile, ApplicabilityDecision, DecisionStatus } from '../types/decision';
import { ControlObligation, StatutoryProvision } from '../types/obligation';
import { StatutoryExceptionRecord } from '../types/exception';

export type ScopeCategory =
  | 'SDF'
  | 'CHILDREN_DATA'
  | 'CROSS_BORDER'
  | 'CONSENT_MANAGERS'
  | 'STATE_ENTITY'
  | 'STATUTORY_EXCEPTION'
  | 'CORE_FIDUCIARY';

export interface ScopeEvaluationItem {
  target_id: string; // e.g. "OBL-01", "ACT-1", "RULE-3"
  target_type: 'control' | 'provision';
  title: string;
  domain: string;
  category: ScopeCategory;
  category_label: string;
  current_status: DecisionStatus;
  recommended_status: DecisionStatus;
  statutory_ground: string;
  trigger_reason: string;
  rationale: string;
  entity_facts: string;
  assigned_owner: string;
  is_changed: boolean;
  provision_ref?: string;
}

export interface ScopeEvaluationReport {
  timestamp: string;
  total_evaluated: number;
  controls_count: number;
  provisions_count: number;
  applicable_count: number;
  not_applicable_count: number;
  changed_count: number;
  items: ScopeEvaluationItem[];
  decisions_to_apply: Record<string, ApplicabilityDecision>;
}

// Control IDs categorized by statutory trigger
export const SDF_SPECIFIC_CONTROLS = ['OBL-33', 'OBL-34', 'OBL-35', 'OBL-36'];
export const CHILDREN_SPECIFIC_CONTROLS = [
  'OBL-24',
  'OBL-25',
  'OBL-26',
  'OBL-27',
  'OBL-28',
  'OBL-29',
  'OBL-30',
  'OBL-31'
];
export const CROSS_BORDER_SPECIFIC_CONTROLS = ['OBL-22', 'OBL-43', 'OBL-44'];
export const CONSENT_MANAGER_SPECIFIC_CONTROLS = ['OBL-46', 'OBL-47'];
export const STATE_ENTITY_CONTROLS = ['OBL-21'];

// Provision & Rule IDs categorized by statutory trigger
export const SDF_SPECIFIC_PROVISIONS = ['ACT-10', 'RULE-13'];
export const CHILDREN_SPECIFIC_PROVISIONS = [
  'ACT-9',
  'RULE-10',
  'RULE-11',
  'RULE-12',
  'RULE-SCHEDULE-IV'
];
export const CROSS_BORDER_SPECIFIC_PROVISIONS = ['ACT-16', 'RULE-15'];
export const CONSENT_MANAGER_SPECIFIC_PROVISIONS = ['ACT-6(9)', 'ACT-6(10)', 'RULE-4'];
export const STATE_ENTITY_PROVISIONS = ['RULE-5', 'ACT-17(2)(a)', 'ACT-17(4)'];

/**
 * Evaluates both Control Obligations (54) and Statutory Provisions/Rules (102)
 * against the current Legal Entity Profile, Statutory Scope flags, and active Statutory Exceptions
 * to determine APPLICABLE vs NOT_APPLICABLE status across the entire compliance register.
 */
export function evaluateEntityScopeApplicability(
  workspace: WorkspaceProfile,
  controls: ControlObligation[],
  provisions: StatutoryProvision[] = [],
  existingDecisions: Record<string, ApplicabilityDecision>,
  exceptions: StatutoryExceptionRecord[] = []
): ScopeEvaluationReport {
  const items: ScopeEvaluationItem[] = [];
  const decisionsToApply: Record<string, ApplicabilityDecision> = {};

  const activeExceptions = exceptions.filter(e => e.status === 'ACTIVE');
  const isGovernmentEntity =
    workspace.entity_type?.toLowerCase().includes('government') ||
    workspace.entity_type?.toLowerCase().includes('statutory') ||
    workspace.entity_type?.toLowerCase().includes('state');

  // =========================================================================
  // 1. EVALUATE CONTROL OBLIGATIONS (54 CONTROLS)
  // =========================================================================
  controls.forEach(ctrl => {
    const existing = existingDecisions[ctrl.id];
    const currentStatus: DecisionStatus = existing?.status || 'UNASSESSED';

    let recommendedStatus: DecisionStatus = 'APPLICABLE';
    let category: ScopeCategory = 'CORE_FIDUCIARY';
    let categoryLabel = 'Baseline Enterprise Duties';
    let statutoryGround = 'DPDP Act 2023 Baseline';
    let triggerReason = 'Mandatory baseline obligation for all Data Fiduciaries operating in India.';
    let rationale = `Mandatory general obligation for all Data Fiduciaries processing digital personal data under the DPDP Act 2023.`;
    let entityFacts = `• Legal Entity: ${workspace.entity_name || 'Data Fiduciary'}\n• Entity Type: ${workspace.entity_type || 'Commercial Corporation'}\n• Baseline compliance evaluation.`;

    // 1. Check if covered by an Active Statutory Exception (Section 17 / Section 3(c))
    const matchedException = activeExceptions.find(
      e => e.mapped_obligation_ids && (e.mapped_obligation_ids.includes(ctrl.id) || e.mapped_obligation_ids.includes(ctrl.title))
    );

    if (matchedException) {
      category = 'STATUTORY_EXCEPTION';
      categoryLabel = `Safe Harbour (${matchedException.statutory_ground})`;
      recommendedStatus = 'NOT_APPLICABLE';
      statutoryGround = matchedException.statutory_ground;
      triggerReason = `Exempt under active Statutory Exception ${matchedException.id} (${matchedException.title}).`;
      rationale = `Exempt under statutory safe harbour ${matchedException.statutory_ground} (${matchedException.title}): ${matchedException.justification}`;
      entityFacts = `• Active Statutory Exception: ${matchedException.id} - ${matchedException.title}\n• Approved by: ${matchedException.approved_by} on ${matchedException.approval_date}\n• Legal Basis: ${matchedException.statutory_ground}`;
    }
    // 2. Significant Data Fiduciary (SDF) Specific Controls (s.10 & r.13)
    else if (SDF_SPECIFIC_CONTROLS.includes(ctrl.id)) {
      category = 'SDF';
      categoryLabel = 'Significant Data Fiduciary (s.10)';
      statutoryGround = 'DPDP Act Section 10 & Rule 13';

      if (!workspace.is_sdf) {
        recommendedStatus = 'NOT_APPLICABLE';
        triggerReason = 'Entity is NOT classified as a Significant Data Fiduciary (is_sdf = false).';
        rationale = `Entity is not designated as a Significant Data Fiduciary (SDF) by Central Government (MeitY) under Section 10(1). Special duties under Section 10(2) & Rule 13 (India-resident statutory DPO, Independent Auditor, mandatory DPIA) do not apply.`;
        entityFacts = `• Legal Entity Profile: is_sdf = false\n• Corporate Designation: Standard Data Fiduciary (Non-SDF)\n• Section 10 enhanced provisions relieved.`;
      } else {
        recommendedStatus = 'APPLICABLE';
        triggerReason = 'Entity IS classified as a Significant Data Fiduciary (is_sdf = true).';
        rationale = `Entity is designated as a Significant Data Fiduciary under Section 10(1). Enhanced obligations under Section 10(2) and Rule 13 are strictly mandatory.`;
        const factors = (workspace.sdf_factors || []).join('; ');
        entityFacts = `• Legal Entity Profile: is_sdf = true\n• Gazette Reference: ${workspace.sdf_gazette_notification || 'Notified under Section 10(1)'}\n• Statutory Factors: ${factors || 'High volume / sensitivity'}\n• Designated DPO: ${workspace.dpo_name || 'Designated'} (${workspace.dpo_email || 'dpo@entity.com'})`;
      }
    }
    // 3. Children's and Disabilities Data Processing (s.9 & r.10-12)
    else if (CHILDREN_SPECIFIC_CONTROLS.includes(ctrl.id)) {
      category = 'CHILDREN_DATA';
      categoryLabel = "Children's Personal Data (s.9)";
      statutoryGround = 'DPDP Act Section 9 & Rules 10-12';

      if (!workspace.processes_children_data && !workspace.processes_disabilities_data) {
        recommendedStatus = 'NOT_APPLICABLE';
        triggerReason = 'Entity does NOT process personal data of children (<18) or persons with disabilities.';
        rationale = `Entity does not process personal data belonging to children under 18 years of age or persons with disabilities. Section 9 parental consent verification, tracking bans, and child-safe defaults do not apply.`;
        entityFacts = `• Legal Entity Profile: processes_children_data = false, processes_disabilities_data = false\n• User Demographic: Exclusively adults (18+)\n• Section 9 provisions relieved.`;
      } else {
        recommendedStatus = 'APPLICABLE';
        triggerReason = 'Entity DOES process personal data of children (<18) or persons with disabilities.';
        rationale = `Entity processes personal data of children or individuals under lawful guardianship. Mandatory verifiable parental consent (s.9(1)), tracking bans (s.9(3)), and child-safe defaults apply.`;
        entityFacts = `• Legal Entity Profile: processes_children_data = ${workspace.processes_children_data}\n• Parental Consent Mechanism: ${workspace.parental_consent_mechanism || 'DigiLocker / Aadhaar Virtual ID'}\n• Age Verification Gate: ${workspace.age_gating_mechanism || 'Active'}`;
      }
    }
    // 4. Cross-Border Transfers (s.16 & r.15)
    else if (CROSS_BORDER_SPECIFIC_CONTROLS.includes(ctrl.id)) {
      category = 'CROSS_BORDER';
      categoryLabel = 'Cross-Border Transfers (s.16)';
      statutoryGround = 'DPDP Act Section 16 & Rule 15';

      if (!workspace.has_cross_border_transfers) {
        recommendedStatus = 'NOT_APPLICABLE';
        triggerReason = 'Entity does NOT transfer personal data outside India (has_cross_border_transfers = false).';
        rationale = `Entity stores and processes all digital personal data strictly within Indian sovereign territory. Cross-border transfer protocols and Negative List screening under Section 16 are not triggered.`;
        entityFacts = `• Legal Entity Profile: has_cross_border_transfers = false\n• Data Residency: Strictly domestic Indian cloud regions / on-premise infrastructure\n• Section 16 provisions relieved.`;
      } else {
        recommendedStatus = 'APPLICABLE';
        triggerReason = 'Entity DOES transfer personal data outside India (has_cross_border_transfers = true).';
        rationale = `Entity transfers or stores personal data outside India. Must comply with Central Government Negative List restrictions, sectoral higher standards, and transfer safeguards under Section 16.`;
        entityFacts = `• Legal Entity Profile: has_cross_border_transfers = true\n• Destination Jurisdictions: ${workspace.cross_border_countries || 'Global Cloud / Overseas Processors'}\n• Transfer Safeguards: ${workspace.cross_border_safeguards || 'Standard Contractual Clauses & Encryption'}`;
      }
    }
    // 5. Consent Managers Interoperability (s.6(9) & r.4)
    else if (CONSENT_MANAGER_SPECIFIC_CONTROLS.includes(ctrl.id)) {
      category = 'CONSENT_MANAGERS';
      categoryLabel = 'Consent Managers (s.6(9))';
      statutoryGround = 'DPDP Act Section 6(9) & Rule 4';

      if (!workspace.uses_consent_managers) {
        recommendedStatus = 'NOT_APPLICABLE';
        triggerReason = 'Entity does NOT use registered Consent Managers (uses_consent_managers = false).';
        rationale = `Entity does not interface with registered Consent Managers and relies exclusively on direct itemised consent capture under Section 6(1). Consent Manager API and interoperability standards under Section 6(9) do not apply.`;
        entityFacts = `• Legal Entity Profile: uses_consent_managers = false\n• Consent Architecture: Direct application notice & token capture\n• Section 6(9) & Rule 4 interoperability relieved.`;
      } else {
        recommendedStatus = 'APPLICABLE';
        triggerReason = 'Entity DOES integrate registered Consent Managers (uses_consent_managers = true).';
        rationale = `Entity interfaces with registered Consent Managers for consent give, review, and withdraw workflows under Section 6(9) and Rule 4.`;
        entityFacts = `• Legal Entity Profile: uses_consent_managers = true\n• Registered CM Gateways: ${workspace.consent_manager_providers || 'MeitY Registered Consent Managers'}\n• Interoperability protocols active.`;
      }
    }
    // 6. State / Government Instrumentality Processing (r.5 & s.17(2)(a))
    else if (STATE_ENTITY_CONTROLS.includes(ctrl.id)) {
      category = 'STATE_ENTITY';
      categoryLabel = 'State Instrumentality (r.5)';
      statutoryGround = 'DPDP Rules Rule 5 & Section 17(2)(a)';

      if (!isGovernmentEntity) {
        recommendedStatus = 'NOT_APPLICABLE';
        triggerReason = `Entity is a private commercial body (${workspace.entity_type || 'Private Limited Company'}).`;
        rationale = `Entity is a private commercial body. Special Rule 5 exemptions for State instrumentalities, national security processing, and sovereign functions do not apply to private fiduciaries.`;
        entityFacts = `• Legal Entity Profile: entity_type = "${workspace.entity_type || 'Private Limited Company'}"\n• Classification: Non-State Commercial Enterprise\n• Rule 5 State exemption not applicable.`;
      } else {
        recommendedStatus = 'APPLICABLE';
        triggerReason = `Entity IS a Government Entity / Statutory Body (${workspace.entity_type}).`;
        rationale = `Entity operates as a State instrumentality or statutory authority subject to Rule 5 state processing standards and governance.`;
        entityFacts = `• Legal Entity Profile: entity_type = "${workspace.entity_type}"\n• Classification: State Instrumentality / Public Body\n• Rule 5 State governance active.`;
      }
    }

    const isChanged = recommendedStatus !== currentStatus;

    // Build the updated ApplicabilityDecision record
    const updatedDecision: ApplicabilityDecision = {
      id: ctrl.id,
      target_id: ctrl.id,
      target_type: 'control',
      status: recommendedStatus,
      entity_facts: entityFacts,
      rationale: rationale,
      assigned_owner: existing?.assigned_owner || ctrl.recommended_owner || 'General Counsel / Legal',
      reviewer_name: existing?.reviewer_name || workspace.dpo_name || 'Automated Scope Engine',
      review_date: new Date().toISOString().slice(0, 10),
      unresolved_blockers: existing?.unresolved_blockers || [],
      systems_in_scope: existing?.systems_in_scope || (ctrl.systems ? ctrl.systems.split(';').map(s => s.trim()).filter(Boolean) : []),
      updated_at: new Date().toISOString(),
      rev: (existing?.rev || 0) + 1
    };

    decisionsToApply[ctrl.id] = updatedDecision;

    items.push({
      target_id: ctrl.id,
      target_type: 'control',
      title: ctrl.title,
      domain: ctrl.domain_slug || ctrl.domain || 'general',
      category,
      category_label: categoryLabel,
      current_status: currentStatus,
      recommended_status: recommendedStatus,
      statutory_ground: statutoryGround,
      trigger_reason: triggerReason,
      rationale,
      entity_facts: entityFacts,
      assigned_owner: updatedDecision.assigned_owner,
      is_changed: isChanged
    });
  });

  // =========================================================================
  // 2. EVALUATE STATUTORY PROVISIONS & RULES (102 PROVISIONS / RULES)
  // =========================================================================
  provisions.forEach(prov => {
    const existing = existingDecisions[prov.id];
    const currentStatus: DecisionStatus = existing?.status || 'UNASSESSED';

    let recommendedStatus: DecisionStatus = 'APPLICABLE';
    let category: ScopeCategory = 'CORE_FIDUCIARY';
    let categoryLabel = 'Statutory Legal Rule';
    let statutoryGround = prov.provision || 'DPDP Statutory Baseline';
    let triggerReason = 'Mandatory statutory baseline provision under the DPDP Act 2023 & DPDP Rules.';
    let rationale = prov.author_interpretation || `Mandatory statutory requirement under ${prov.provision} for processing digital personal data.`;
    let entityFacts = `• Legal Entity: ${workspace.entity_name || 'Data Fiduciary'}\n• Entity Type: ${workspace.entity_type || 'Commercial Corporation'}\n• Statutory provision compliance review.`;

    // 1. Check if covered by an Active Statutory Exception
    const matchedException = activeExceptions.find(
      e => e.mapped_obligation_ids && (e.mapped_obligation_ids.includes(prov.id) || e.mapped_obligation_ids.includes(prov.provision))
    );

    if (matchedException) {
      category = 'STATUTORY_EXCEPTION';
      categoryLabel = `Safe Harbour (${matchedException.statutory_ground})`;
      recommendedStatus = 'NOT_APPLICABLE';
      statutoryGround = matchedException.statutory_ground;
      triggerReason = `Exempt under active Statutory Exception ${matchedException.id} (${matchedException.title}).`;
      rationale = `Exempt under statutory safe harbour ${matchedException.statutory_ground} (${matchedException.title}): ${matchedException.justification}`;
      entityFacts = `• Active Statutory Exception: ${matchedException.id} - ${matchedException.title}\n• Approved by: ${matchedException.approved_by} on ${matchedException.approval_date}\n• Legal Basis: ${matchedException.statutory_ground}`;
    }
    // 2. Significant Data Fiduciary (SDF) Provisions & Rules (s.10 & r.13)
    else if (SDF_SPECIFIC_PROVISIONS.includes(prov.id)) {
      category = 'SDF';
      categoryLabel = 'Significant Data Fiduciary (s.10)';
      statutoryGround = `${prov.provision} (SDF Statutory Duty)`;

      if (!workspace.is_sdf) {
        recommendedStatus = 'NOT_APPLICABLE';
        triggerReason = 'Entity is NOT classified as a Significant Data Fiduciary (is_sdf = false).';
        rationale = `Entity is not designated as a Significant Data Fiduciary under Section 10(1). Statutory obligations under ${prov.provision} (India-resident statutory DPO, Independent Auditor, mandatory DPIA) do not apply.`;
        entityFacts = `• Legal Entity Profile: is_sdf = false\n• Corporate Designation: Standard Data Fiduciary (Non-SDF)\n• ${prov.provision} statutory duties relieved.`;
      } else {
        recommendedStatus = 'APPLICABLE';
        triggerReason = 'Entity IS classified as a Significant Data Fiduciary (is_sdf = true).';
        rationale = `Entity is designated as a Significant Data Fiduciary under Section 10(1). Statutory mandate under ${prov.provision} is active and legally binding.`;
        const factors = (workspace.sdf_factors || []).join('; ');
        entityFacts = `• Legal Entity Profile: is_sdf = true\n• Gazette Reference: ${workspace.sdf_gazette_notification || 'Notified under Section 10(1)'}\n• Statutory Factors: ${factors || 'High volume / sensitivity'}`;
      }
    }
    // 3. Children's and Disabilities Data Processing Provisions & Rules (s.9 & r.10-12)
    else if (CHILDREN_SPECIFIC_PROVISIONS.includes(prov.id)) {
      category = 'CHILDREN_DATA';
      categoryLabel = "Children's Personal Data (s.9)";
      statutoryGround = `${prov.provision} (Children Data Processing)`;

      if (!workspace.processes_children_data && !workspace.processes_disabilities_data) {
        recommendedStatus = 'NOT_APPLICABLE';
        triggerReason = 'Entity does NOT process personal data of children (<18) or persons with disabilities.';
        rationale = `Entity does not process personal data belonging to children under 18 years of age or persons with disabilities. Statutory provisions under ${prov.provision} (verifiable parental consent, tracking bans, behavioural monitoring) do not apply.`;
        entityFacts = `• Legal Entity Profile: processes_children_data = false, processes_disabilities_data = false\n• User Demographic: Exclusively adults (18+)\n• ${prov.provision} provisions relieved.`;
      } else {
        recommendedStatus = 'APPLICABLE';
        triggerReason = 'Entity DOES process personal data of children (<18) or persons with disabilities.';
        rationale = `Entity processes personal data of children or individuals under lawful guardianship. Statutory mandate under ${prov.provision} applies.`;
        entityFacts = `• Legal Entity Profile: processes_children_data = ${workspace.processes_children_data}\n• Parental Consent Mechanism: ${workspace.parental_consent_mechanism || 'DigiLocker / Aadhaar Virtual ID'}\n• Age Verification Gate: ${workspace.age_gating_mechanism || 'Active'}`;
      }
    }
    // 4. Cross-Border Transfers Provisions & Rules (s.16 & r.15)
    else if (CROSS_BORDER_SPECIFIC_PROVISIONS.includes(prov.id)) {
      category = 'CROSS_BORDER';
      categoryLabel = 'Cross-Border Transfers (s.16)';
      statutoryGround = `${prov.provision} (Cross-Border Transfer)`;

      if (!workspace.has_cross_border_transfers) {
        recommendedStatus = 'NOT_APPLICABLE';
        triggerReason = 'Entity does NOT transfer personal data outside India (has_cross_border_transfers = false).';
        rationale = `Entity processes and stores all personal data strictly within Indian sovereign territory. Statutory restrictions and Negative List compliance under ${prov.provision} do not apply.`;
        entityFacts = `• Legal Entity Profile: has_cross_border_transfers = false\n• Data Residency: Strictly domestic Indian cloud regions\n• ${prov.provision} provisions relieved.`;
      } else {
        recommendedStatus = 'APPLICABLE';
        triggerReason = 'Entity DOES transfer personal data outside India (has_cross_border_transfers = true).';
        rationale = `Entity transfers or stores personal data outside India. Compliance with ${prov.provision} and Central Government cross-border transfer directives is mandatory.`;
        entityFacts = `• Legal Entity Profile: has_cross_border_transfers = true\n• Destination Jurisdictions: ${workspace.cross_border_countries || 'Global Cloud / Overseas Processors'}\n• Safeguards: ${workspace.cross_border_safeguards || 'Standard Contractual Clauses & Encryption'}`;
      }
    }
    // 5. Consent Managers Provisions & Rules (s.6(9), s.6(10) & r.4)
    else if (CONSENT_MANAGER_SPECIFIC_PROVISIONS.includes(prov.id)) {
      category = 'CONSENT_MANAGERS';
      categoryLabel = 'Consent Managers (s.6(9))';
      statutoryGround = `${prov.provision} (Consent Managers)`;

      if (!workspace.uses_consent_managers) {
        recommendedStatus = 'NOT_APPLICABLE';
        triggerReason = 'Entity does NOT use registered Consent Managers (uses_consent_managers = false).';
        rationale = `Entity does not interface with registered Consent Managers and relies exclusively on direct itemised notice/consent capture under Section 6(1). Statutory standards under ${prov.provision} do not apply.`;
        entityFacts = `• Legal Entity Profile: uses_consent_managers = false\n• Consent Architecture: Direct application notice & token capture\n• ${prov.provision} standards relieved.`;
      } else {
        recommendedStatus = 'APPLICABLE';
        triggerReason = 'Entity DOES integrate registered Consent Managers (uses_consent_managers = true).';
        rationale = `Entity interfaces with registered Consent Managers for consent capture and management under ${prov.provision}.`;
        entityFacts = `• Legal Entity Profile: uses_consent_managers = true\n• Registered CM Gateways: ${workspace.consent_manager_providers || 'MeitY Registered Consent Managers'}\n• Interoperability protocols active.`;
      }
    }
    // 6. State / Government Instrumentality Processing (r.5 & s.17(2)(a), s.17(4))
    else if (STATE_ENTITY_PROVISIONS.includes(prov.id)) {
      category = 'STATE_ENTITY';
      categoryLabel = 'State Instrumentality (r.5 / s.17)';
      statutoryGround = `${prov.provision} (State Functions)`;

      if (!isGovernmentEntity) {
        recommendedStatus = 'NOT_APPLICABLE';
        triggerReason = `Entity is a private commercial body (${workspace.entity_type || 'Private Limited Company'}).`;
        rationale = `Entity is a private commercial body. Special statutory provisions for State instrumentalities and sovereign functions under ${prov.provision} do not apply to private fiduciaries.`;
        entityFacts = `• Legal Entity Profile: entity_type = "${workspace.entity_type || 'Private Limited Company'}"\n• Classification: Non-State Commercial Enterprise\n• ${prov.provision} State processing exemption not applicable.`;
      } else {
        recommendedStatus = 'APPLICABLE';
        triggerReason = `Entity IS a Government Entity / Statutory Body (${workspace.entity_type}).`;
        rationale = `Entity operates as a State instrumentality subject to ${prov.provision}.`;
        entityFacts = `• Legal Entity Profile: entity_type = "${workspace.entity_type}"\n• Classification: State Instrumentality / Public Body\n• ${prov.provision} active.`;
      }
    }

    const isChanged = recommendedStatus !== currentStatus;

    // Build the updated ApplicabilityDecision record for provision
    const updatedDecision: ApplicabilityDecision = {
      id: prov.id,
      target_id: prov.id,
      target_type: 'provision',
      status: recommendedStatus,
      entity_facts: entityFacts,
      rationale: rationale,
      assigned_owner: existing?.assigned_owner || 'General Counsel / Legal',
      reviewer_name: existing?.reviewer_name || workspace.dpo_name || 'Automated Scope Engine',
      review_date: new Date().toISOString().slice(0, 10),
      unresolved_blockers: existing?.unresolved_blockers || [],
      systems_in_scope: existing?.systems_in_scope || [],
      updated_at: new Date().toISOString(),
      rev: (existing?.rev || 0) + 1
    };

    decisionsToApply[prov.id] = updatedDecision;

    const displayTitle = prov.provision ? `${prov.provision} — ${prov.trigger || prov.actor || prov.id}` : prov.id;

    items.push({
      target_id: prov.id,
      target_type: 'provision',
      title: displayTitle,
      domain: prov.domain_slug || prov.domain || 'legal',
      category,
      category_label: categoryLabel,
      current_status: currentStatus,
      recommended_status: recommendedStatus,
      statutory_ground: statutoryGround,
      trigger_reason: triggerReason,
      rationale,
      entity_facts: entityFacts,
      assigned_owner: updatedDecision.assigned_owner,
      is_changed: isChanged,
      provision_ref: prov.provision
    });
  });

  const applicableCount = items.filter(i => i.recommended_status === 'APPLICABLE').length;
  const notApplicableCount = items.filter(i => i.recommended_status === 'NOT_APPLICABLE').length;
  const changedCount = items.filter(i => i.is_changed).length;

  return {
    timestamp: new Date().toISOString(),
    total_evaluated: items.length,
    controls_count: controls.length,
    provisions_count: provisions.length,
    applicable_count: applicableCount,
    not_applicable_count: notApplicableCount,
    changed_count: changedCount,
    items,
    decisions_to_apply: decisionsToApply
  };
}

export interface ExceptionEvaluationItem {
  target_id: string;
  target_type: 'control' | 'provision';
  title: string;
  domain: string;
  exception_id: string;
  exception_title: string;
  statutory_ground: string;
  category: string;
  justification: string;
  approved_by: string;
  approval_date: string;
  current_status: DecisionStatus;
  recommended_status: DecisionStatus;
  rationale: string;
  entity_facts: string;
  is_changed: boolean;
  provision_ref?: string;
}

export interface ExceptionsEvaluationReport {
  timestamp: string;
  total_exceptions_count: number;
  active_exceptions_count: number;
  total_mapped_obligations_count: number;
  unique_controls_count: number;
  unique_provisions_count: number;
  changed_count: number;
  items: ExceptionEvaluationItem[];
  decisions_to_apply: Record<string, ApplicabilityDecision>;
}

/**
 * Evaluates active Statutory Exceptions (s.17 & s.3(c)) against both Control Obligations (54)
 * and Statutory Provisions/Rules (102) and generates ApplicabilityDecisions marking mapped items
 * as NOT_APPLICABLE with formal legal rationale.
 */
export function evaluateExceptionsApplicability(
  exceptions: StatutoryExceptionRecord[],
  controls: ControlObligation[],
  provisions: StatutoryProvision[] = [],
  existingDecisions: Record<string, ApplicabilityDecision>,
  targetExceptionId?: string
): ExceptionsEvaluationReport {
  const activeExceptions = exceptions.filter(
    e => e.status === 'ACTIVE' && (!targetExceptionId || e.id === targetExceptionId)
  );
  const items: ExceptionEvaluationItem[] = [];
  const decisionsToApply: Record<string, ApplicabilityDecision> = {};
  const seenControls = new Set<string>();
  const seenProvisions = new Set<string>();

  activeExceptions.forEach(ex => {
    (ex.mapped_obligation_ids || []).forEach(oblId => {
      const ctrl = controls.find(c => c.id === oblId);
      const prov = provisions.find(p => p.id === oblId || p.provision === oblId);
      const isProvision = !ctrl && (!!prov || !oblId.startsWith('OBL-'));
      const targetType: 'control' | 'provision' = isProvision ? 'provision' : 'control';

      const existing = decisionsToApply[oblId] || existingDecisions[oblId];
      const currentStatus: DecisionStatus = existingDecisions[oblId]?.status || 'UNASSESSED';
      const recommendedStatus: DecisionStatus = 'NOT_APPLICABLE';
      const isChanged = currentStatus !== recommendedStatus;

      const entityFacts = existing?.entity_facts
        ? `${existing.entity_facts}\n• Statutory Exception: ${ex.statutory_ground} - ${ex.title} (Approved by ${ex.approved_by} on ${ex.approval_date})`
        : `• Statutory Exception: ${ex.statutory_ground} - ${ex.title}\n• Approved by: ${ex.approved_by} on ${ex.approval_date}\n• Legal Basis: ${ex.statutory_ground}`;

      const rationale = `Exempt under statutory safe harbour ${ex.statutory_ground} (${ex.title}): ${ex.justification}`;

      const itemTitle = ctrl?.title || (prov ? `${prov.provision} — ${prov.trigger || prov.actor || prov.id}` : oblId);
      const itemDomain = ctrl?.domain_slug || ctrl?.domain || prov?.domain_slug || prov?.domain || 'general';

      const updatedDecision: ApplicabilityDecision = {
        id: oblId,
        target_id: oblId,
        target_type: targetType,
        status: recommendedStatus,
        entity_facts: entityFacts,
        rationale: rationale,
        assigned_owner: existing?.assigned_owner || ctrl?.recommended_owner || 'General Counsel / Legal',
        reviewer_name: ex.approved_by || existing?.reviewer_name || 'Compliance Lead',
        review_date: ex.approval_date || new Date().toISOString().slice(0, 10),
        unresolved_blockers: existing?.unresolved_blockers || [],
        systems_in_scope: existing?.systems_in_scope || (ctrl?.systems ? ctrl.systems.split(';').map(s => s.trim()).filter(Boolean) : []),
        updated_at: new Date().toISOString(),
        rev: (existing?.rev || 0) + 1
      };

      decisionsToApply[oblId] = updatedDecision;
      if (targetType === 'control') {
        seenControls.add(oblId);
      } else {
        seenProvisions.add(oblId);
      }

      items.push({
        target_id: oblId,
        target_type: targetType,
        title: itemTitle,
        domain: itemDomain,
        exception_id: ex.id,
        exception_title: ex.title,
        statutory_ground: ex.statutory_ground,
        category: ex.category,
        justification: ex.justification,
        approved_by: ex.approved_by,
        approval_date: ex.approval_date,
        current_status: currentStatus,
        recommended_status: recommendedStatus,
        rationale,
        entity_facts: entityFacts,
        is_changed: isChanged,
        provision_ref: prov?.provision
      });
    });
  });

  const allSeenIds = [...Array.from(seenControls), ...Array.from(seenProvisions)];
  const changedCount = allSeenIds.filter(
    id => existingDecisions[id]?.status !== 'NOT_APPLICABLE'
  ).length;

  return {
    timestamp: new Date().toISOString(),
    total_exceptions_count: exceptions.length,
    active_exceptions_count: activeExceptions.length,
    total_mapped_obligations_count: items.length,
    unique_controls_count: seenControls.size,
    unique_provisions_count: seenProvisions.size,
    changed_count: changedCount,
    items,
    decisions_to_apply: decisionsToApply
  };
}
